import { supabase } from "../supabase/supabase";
import { Campaign, CampaignInsert, CampaignUpdate, CampaignStatus } from "@/types/campaign";
import { featureTemplateService } from "./featureTemplateService";

export const campaignService = {
  /**
   * Create a new campaign
   */
  async createCampaign(
    campaign: CampaignInsert,
  ): Promise<{ data: Campaign | null; error: Error | null }> {
    try {
      // Validate required fields
      if (!campaign.phone_number_id) {
        throw new Error("Phone number is required");
      }

      const { data, error } = await supabase
        .from("campaigns")
        .insert(campaign)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error creating campaign:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get all campaigns for an organization
   */
  async getCampaigns(
    organizationId: string,
  ): Promise<{ data: Campaign[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting campaigns:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get a campaign by ID
   */
  async getCampaign(
    id: string,
  ): Promise<{ data: Campaign | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaigns")
        .select(`
          *,
          feature_templates (
            id,
            name,
            schema_template:schema_templates (
              id,
              name,
              schema
            )
          )
        `)
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting campaign:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update a campaign
   */
  async updateCampaign(
    id: string,
    updates: CampaignUpdate,
  ): Promise<{ data: Campaign | null; error: Error | null }> {
    try {
      // If phone_number_id is being updated, validate it exists
      if (updates.phone_number_id !== undefined && !updates.phone_number_id) {
        throw new Error("Phone number is required");
      }

      const { data, error } = await supabase
        .from("campaigns")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error updating campaign:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update campaign status
   */
  async updateCampaignStatus(
    id: string,
    status: CampaignStatus,
  ): Promise<{ data: Campaign | null; error: Error | null }> {
    return this.updateCampaign(id, { status });
  },

  /**
   * Delete a campaign
   */
  async deleteCampaign(
    id: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      // First delete all campaign leads
      const { error: deleteLeadsError } = await supabase
        .from("campaign_leads")
        .delete()
        .eq("campaign_id", id);

      if (deleteLeadsError) {
        throw deleteLeadsError;
      }

      // Then delete the campaign
      const { error } = await supabase
        .from("campaigns")
        .delete()
        .eq("id", id);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error deleting campaign:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Add leads to a campaign
   */
  async addLeadsToCampaign(
    campaignId: string,
    leadIds: string[],
  ): Promise<{ success: boolean; error: Error | null }> {
    // First get the campaign to check the feature template
    const { data: campaign, error: campaignError } = await supabase
      .from("campaigns")
      .select("*, feature_templates(*)")
      .eq("id", campaignId)
      .single();

    if (campaignError) throw campaignError;
    if (!campaign) throw new Error("Campaign not found");

    // Get the feature template to check limits
    const featureTemplate = await featureTemplateService.getFeatureTemplate(
      campaign.feature_template_id
    );

    if (!featureTemplate) throw new Error("Feature template not found");

    // If there's a leads limit, check it
    if (featureTemplate.max_leads_per_instance !== undefined && 
        featureTemplate.max_leads_per_instance !== null) {
      // Get current lead count
      const { data: currentLeads, error: countError } = await supabase
        .from("campaign_leads")
        .select("id", { count: "exact" })
        .eq("campaign_id", campaignId);

      if (countError) throw countError;

      const currentCount = (currentLeads?.length || 0);
      const newTotal = currentCount + leadIds.length;

      if (newTotal > featureTemplate.max_leads_per_instance) {
        throw new Error(`Adding these leads would exceed the maximum limit of ${featureTemplate.max_leads_per_instance} leads for this campaign. Current: ${currentCount}, Attempting to add: ${leadIds.length}`);
      }
    }

    // Add the leads if we're within limits
    const campaignLeads = leadIds.map((leadId) => ({
      campaign_id: campaignId,
      lead_id: leadId,
    }));

    const { error } = await supabase
      .from("campaign_leads")
      .insert(campaignLeads);

    return { success: !error, error };
  },

  /**
   * Remove a lead from a campaign
   */
  async removeLeadFromCampaign(
    campaignId: string,
    leadId: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("campaign_leads")
        .delete()
        .eq("campaign_id", campaignId)
        .eq("lead_id", leadId);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Error removing lead from campaign:", error);
      return { success: false, error: error as Error };
    }
  },

  /**
   * Get all leads for a campaign
   */
  async getCampaignLeads(
    campaignId: string,
  ): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("campaign_leads")
        .select(
          `
          id,
          added_at,
          leads:lead_id (*)
        `,
        )
        .eq("campaign_id", campaignId);

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error("Error getting campaign leads:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get campaign statistics
   */
  async getCampaignStats(
    campaignId: string,
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      // Get the total number of leads in the campaign
      const { count, error: countError } = await supabase
        .from("campaign_leads")
        .select("id", { count: "exact" })
        .eq("campaign_id", campaignId);

      if (countError) {
        throw countError;
      }

      return {
        data: {
          totalLeads: count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error("Error getting campaign stats:", error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get CallFlow queue status (real-time queue monitoring)
   */
  async getCallFlowQueueStatus(
    callflowId: string,
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      const callServiceUrl = import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8765";
      const response = await fetch(`${callServiceUrl}/callflow/${callflowId}/queue-status`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      console.error("Error getting CallFlow queue status:", error);
      return { data: null, error: error as Error };
    }
  },
};